import type { MaterialsNodeData, TimberListItem } from '../types';
import {
  abbreviateSpeciesName,
  abbreviateTimberDrynessDisplay,
  expandTimberDryness,
} from './timberDisplayLabels';

/** Middle dot separator for client-facing spec labels (readable with hyphenated species). */
const CLIENT_SPEC_SEPARATOR = ' \u00b7 ';

export interface ProposalTimberDefaults {
  species: string;
  grade: string;
  moistureContent: '' | 'green' | 'kiln_dried';
}

export type TimberPiecePriceLookup = (
  species: string,
  grade: string,
  dimensions: string,
  moistureRaw?: string
) => number;

function isExplicitMoisture(
  moisture: TimberListItem['moistureContent'],
): moisture is 'green' | 'kiln_dried' {
  return moisture === 'green' || moisture === 'kiln_dried';
}

/** True when more than one row has an explicit dryness selection. */
export function timberListHasVariedMoisture(list: TimberListItem[]): boolean {
  const values = new Set<'green' | 'kiln_dried'>();
  for (const item of list) {
    if (isExplicitMoisture(item.moistureContent)) {
      values.add(item.moistureContent);
      if (values.size > 1) return true;
    }
  }
  return false;
}

/** Infer defaults from timber rows; only reuse dryness when every configured row agrees. */
export function inferTimberDefaultsFromList(list: TimberListItem[]): ProposalTimberDefaults | null {
  const configured = list.filter(item => item.species?.trim());
  if (configured.length === 0) return null;

  const first = configured[0]!;
  const moistureValues = new Set<'green' | 'kiln_dried'>();
  for (const item of configured) {
    if (isExplicitMoisture(item.moistureContent)) {
      moistureValues.add(item.moistureContent);
    }
  }

  const sharedMoisture =
    moistureValues.size === 1 ? Array.from(moistureValues)[0]! : '';
  return timberDefaultsFromFields(first.species, first.grade, sharedMoisture);
}

export function timberDefaultsFromFields(
  species: string,
  grade: string,
  moistureContent?: '' | 'green' | 'kiln_dried',
): ProposalTimberDefaults | null {
  if (!species?.trim()) return null;
  return {
    species: species.trim(),
    grade: grade || '',
    moistureContent:
      moistureContent === 'green' || moistureContent === 'kiln_dried' ? moistureContent : '',
  };
}

/** Prefer species/grade/dryness already used on this structure in the take-off session. */
export function inferTimberDefaultsForTakeoffStructure(
  structureId: string,
  links: Array<{ structureId: string; species: string; grade: string; moistureContent?: '' | 'green' | 'kiln_dried' }>,
  sessionDefaults: ProposalTimberDefaults | null,
): ProposalTimberDefaults | null {
  const fromStructure = links.find(l => l.structureId === structureId && l.species?.trim());
  if (fromStructure) {
    return timberDefaultsFromFields(
      fromStructure.species,
      fromStructure.grade,
      fromStructure.moistureContent,
    );
  }
  return sessionDefaults;
}

export function applyTimberDefaultsToTimberList(
  list: TimberListItem[],
  defaults: ProposalTimberDefaults,
  getPiecePrice: TimberPiecePriceLookup,
  options?: { preserveExplicitMoisture?: boolean },
): TimberListItem[] {
  const preserveExplicitMoisture = options?.preserveExplicitMoisture !== false;

  return list.map(item => {
    const moistureContent =
      preserveExplicitMoisture && isExplicitMoisture(item.moistureContent)
        ? item.moistureContent
        : defaults.moistureContent;

    const updated: TimberListItem = {
      ...item,
      species: defaults.species,
      grade: defaults.grade,
      moistureContent,
    };
    const unitCost = getPiecePrice(
      updated.species,
      updated.grade,
      updated.dimensions,
      updated.moistureContent,
    );
    return {
      ...updated,
      unitCost,
      total: unitCost * updated.quantity,
    };
  });
}

/** Compact label for one timber list, e.g. "#1 DF KD". */
export function formatTimberListSpecLabel(list: TimberListItem[]): string {
  const inferred = inferTimberDefaultsFromList(list);
  if (!inferred) return '';

  const parts: string[] = [];
  if (inferred.grade.trim()) parts.push(inferred.grade.trim());
  parts.push(abbreviateSpeciesName(inferred.species));
  const dry = abbreviateTimberDrynessDisplay(inferred.moistureContent);
  if (dry !== '—') parts.push(dry);
  return parts.join(' ');
}

/** Title-case a species string for client-facing display (data stays unchanged). */
function titleCaseSpecies(species: string): string {
  return species
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Client-facing spec label: full species name plus grade and dryness when
 * available, e.g. "Douglas Fir · #1 · Kiln Dried". Falls back to species-only
 * (or '' when no species). Grade/dryness are inferred from `list`; pass
 * `displaySpecies` to force the species name (e.g. a selected alternate).
 */
export function formatClientTimberSpecLabel(
  list: TimberListItem[],
  displaySpecies?: string,
): string {
  const inferred = inferTimberDefaultsFromList(list);
  const rawSpecies = displaySpecies?.trim() || inferred?.species || '';
  const species = titleCaseSpecies(rawSpecies);
  if (!species) return '';

  const parts: string[] = [species];
  const grade = inferred?.grade?.trim();
  if (grade) parts.push(grade);
  const dry = expandTimberDryness(inferred?.moistureContent);
  if (dry) parts.push(dry);
  return parts.join(CLIENT_SPEC_SEPARATOR);
}

/**
 * Resolve the timber list backing a species selection so per-option grade and
 * dryness come from that species' own rows. Returns the default list for
 * 'default'/unknown selections, or the matching alternate species' list.
 */
export function timberListForSpeciesSelection(
  materials: MaterialsNodeData,
  selectionKey?: string | null,
): TimberListItem[] {
  const key = (selectionKey || '').trim();
  if (key && key !== 'default') {
    const alt = (materials.alternateSpecies || []).find(s => s.speciesName === key);
    if (alt) return alt.timberList || [];
  }
  return materials.timberList || [];
}

/** Comma-separated timber specs for structure cards when multiple species are quoted. */
export function formatStructureTimberSpecSummary(materials: MaterialsNodeData): string {
  const labels: string[] = [];

  const defaultLabel = formatTimberListSpecLabel(materials.timberList || []);
  if (defaultLabel) labels.push(defaultLabel);

  if (materials.quoteMultipleSpecies && (materials.alternateSpecies?.length ?? 0) > 0) {
    for (const alt of materials.alternateSpecies!) {
      const label = formatTimberListSpecLabel(alt.timberList || []);
      if (label) labels.push(label);
    }
  }

  return labels.join(', ');
}
